import {
  useInfiniteQuery,
  type UseInfiniteQueryOptions,
  type InfiniteData,
} from '@tanstack/react-query';
import {
  fetchLeaderboard,
  type LeaderboardResponse,
  type FetchLeaderboardParams,
} from '@/lib/network';

const LEADERBOARD_STALE_TIME_MS = 2 * 60 * 1000;
const LEADERBOARD_GC_TIME_MS = 5 * 60 * 1000;

export type UseFetchLeaderboardOptions<TData = InfiniteData<LeaderboardResponse>> = Omit<
  UseInfiniteQueryOptions<LeaderboardResponse, Error, TData>,
  'queryKey' | 'queryFn' | 'getNextPageParam' | 'initialPageParam'
>;

/**
 * Infinite-scrolling leaderboard fetch. Pages are keyed by `offset`, advancing
 * by the page's own `limit` so the caller's page size fully controls the step.
 */
export const useFetchLeaderboard = <TData = InfiniteData<LeaderboardResponse>>(
  params: Omit<FetchLeaderboardParams, 'offset'> | null,
  options?: UseFetchLeaderboardOptions<TData>
) => {
  return useInfiniteQuery<LeaderboardResponse, Error, TData>({
    queryKey: ['leaderboard', params],
    queryFn: async ({ pageParam }) => {
      if (!params) {
        throw new Error('Missing leaderboard params');
      }
      return fetchLeaderboard({ ...params, offset: pageParam as number });
    },
    getNextPageParam: lastPage => {
      const { hasMore, offset, limit } = lastPage.data.pagination;
      return hasMore ? offset + limit : undefined;
    },
    initialPageParam: 0,
    enabled: !!params,
    staleTime: LEADERBOARD_STALE_TIME_MS,
    gcTime: LEADERBOARD_GC_TIME_MS,
    ...options,
  });
};
